import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * KitchNest's design constants.
 *
 * Named KitchNest in the gallery, `feast` on disk. The id is the folder name
 * and a merchant's stored theme is keyed on it - `preset`, `layout.family` and
 * every `feast.*` content override - so it stays put; the label is the only
 * part a merchant reads. Decor and PowerTools do the same thing.
 *
 * A homeware shop that photographs its range on white and lets the pictures
 * carry the page: no borders, no fills, no tint anywhere except the two
 * colours below. Almost everything here is in service of that - the page runs
 * nearly edge to edge, the sections stand a long way apart, and the only thing
 * on the page allowed to shout is one word of every heading, set in a brush
 * script. The half of the theme with no React in it.
 */
export const tokens: ThemeTokens = {
  // No heading face of its own: this theme sets its headings in the same sans
  // as its body copy, heavier. That is the whole type system apart from the
  // script below, and a second serif in it would be a different shop.
  headingFont: null,
  // The wordmark's sand, and the theme's signature. Not the primary: the
  // primary is a burnt orange a merchant may repaint, while this is the colour
  // the announcement rail, the marquee band and the footer's headings are
  // drawn with, and it belongs to the drawing rather than to a palette.
  //
  // Deliberately too pale to set small text on the page in - nothing shared
  // reaches for the accent, and everything in this theme that wears it wears
  // it on the near-black ground where it is the legible half of the pair.
  accent: '#eac58e',
  // Prices are set in the body face here. A homeware shop is read by eye, and
  // a machine face under a photograph of a stoneware plate is a different
  // shop's typography.
  monoFont: null,
  // The face for the one word of a heading this theme leans on - "Shop by
  // *Categories*", "*Elevate* the Everyday". One weight is all it publishes;
  // see `FAMILY_WEIGHTS` in `features/storefront/fonts.ts`, which is where
  // that is declared so the whole font request does not fail over it.
  displayFont: 'Kaushan Script',
  // Every heading on this shop is centred over what it introduces, which is
  // what makes the page read as a sequence of rooms rather than a column.
  headingAlign: 'center',
  headingTracking: '-0.01em',
  // Sentence case throughout. The script word is the emphasis, and capitals
  // over the top of it would be two kinds of shouting in one line.
  headingTransform: 'none',
  // 1792px, and the widest in the set by a long way. This shop is drawn to run
  // to the window's edges with a gutter and nothing more: fourteen shelves
  // seven across is the row this width buys, and it is the row the home page
  // opens its catalogue with.
  containerMax: '112rem',
  // Well over 1. The pictures are cut out on white and the page has no rules,
  // no borders and no tints to separate one band from the next - so the space
  // between them is the separation, and it has to be generous enough to do
  // that job on its own.
  spaceScale: 1.35,
  // The announcement rail and the masthead under it.
  headerHeight: '7.5rem',
};

export const meta: ThemeMeta = {
  label: 'KitchNest',
  hint: 'A warm homeware shop: cut-out photography on white, sand and burnt orange, and a script word in every heading.',
  industries: ['home_kitchen', 'furniture'],
  look: {
    /**
     * Two colours and a near-black, which is the whole palette:
     *
     * - the burnt orange is the *primary*, and so the one a merchant repaints:
     *   every button, every sale badge, every price that has dropped;
     * - the near-black is text, and the ground of the announcement rail and
     *   the footer, which this theme inverts rather than tints;
     * - the sand is the theme's own `accent` in `tokens`, not a merchant
     *   setting - see the note there.
     *
     * The background is white and the surface a hair off it, on purpose: a
     * cut-out photograph of a pale stoneware plate sits on the surface colour,
     * and a tinted tile behind it would put a colour cast on the range.
     */
    colors: {
      primary: '#cc4b0a',
      background: '#ffffff',
      surface: '#fafafa',
      text: '#2c2b29',
      muted: '#858585',
    },
    // One face doing both jobs, and `heading_font` left empty on purpose: it
    // is what says "headings are the body face" rather than pinning a second
    // copy of the same name into the document, where the two can go out of
    // step. Same treatment as Voltic's Archivo.
    typography: {
      font_family: 'DM Sans',
      heading_weight: '700',
    },
    // Square corners. The range is photographed against square frames and the
    // tiles are those frames; a radius on them would round the photographs as
    // well, because the picture and its ground share this one number.
    buttons: { radius_px: 0, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`. A kitchen
    // shop sold by colourway - the same casserole in mist, blush and clay - is
    // part of the design, and none of the six industry shops is stocked that
    // way.
  },
};
